import React from 'react';
import { db } from '../../config-firebase/firebase.js';
import { doc, getDoc } from 'firebase/firestore';
import { setDoc, updateDoc } from '../../api/FirestoreInterceptor';
import { getAnonymousUser } from '../../Hooks/useEnsureAnonymousUser.js';
import { cleanObject } from './stockOperations';

/**
 * Hook para gerenciar operações de edição de pedidos em andamento
 * e fluxos de cancelamento ou recusa/ajuste de itens na cozinha.
 */
export const usePdvEditAndRefusal = ({ global, canCancelOrders }) => {
  const [refusalModalData, setRefusalModalData] = React.useState(null);

  const handleEditOrder = async (item) => {
    if (global.orderBeingEdited) {
      alert(
        'Já existe um pedido sendo editado. Finalize ou cancele a edição anterior para editar outro pedido.'
      );
      return;
    }

    try {
      global.setOrderBeingEdited({
        id: item.id,
        countRequest: item.countRequest,
        dateTime: item.dateTime,
        discount: item.discount || 0,
        tableNumber: item.tableNumber || null,
      });

      localStorage.setItem(
        'userMenu',
        JSON.stringify({ id: item.idUser, name: item.name })
      );

      if (item.tableNumber) {
        localStorage.setItem('tableNumber', item.tableNumber);
      } else {
        localStorage.removeItem('tableNumber');
      }

      const cleanArray =
        item.request && item.request.length > 0
          ? item.request.map((r, idx) => ({
              ...cleanObject(r),
              sentToKitchen: true,
              parentRequestId: item.id,
              indexInRequest:
                r.indexInRequest !== undefined ? r.indexInRequest : idx,
              pronto: Boolean(r.pronto),
              entregue: Boolean(r.entregue),
              deliveredByWaiter: Boolean(r.deliveredByWaiter),
              status: r.entregue ? 'Entregue' : (r.pronto ? 'Pronto' : r.status || ''),
            }))
          : [];

      if (item.idUser) {
        const userDocRef = doc(db, 'user', item.idUser);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const uData = userDocSnap.data();
          const existingUserRequests = Array.isArray(uData.request) ? uData.request : [];
          const otherItems = existingUserRequests.filter(
            (it) => it.parentRequestId && it.parentRequestId !== item.id
          );
          await updateDoc(userDocRef, {
            request: [...otherItems, ...cleanArray],
          });
        } else {
          await setDoc(userDocRef, {
            name: item.name || 'anonimo',
            phone: '777',
            birthday: '77',
            email: 'anonimo@anonimo.com',
            request: cleanArray,
          });
        }
      }

      global.setPdvRequest(true);

      if (window.innerWidth < 1000) {
        global.setPdvMobileView('menu');
      }
    } catch (error) {
      console.error('Error starting order edit:', error);
      alert('Houve um erro ao tentar editar o pedido. Tente novamente.');
      global.setOrderBeingEdited(null);
    }
  };

  const handleReleaseEditFromList = async () => {
    global.setOrderBeingEdited(null);
    global.setPdvRequest(false);
    if (localStorage.getItem('pdv') === 'true') {
      localStorage.removeItem('tableNumber');
      try {
        const anonUser = await getAnonymousUser();
        if (anonUser && anonUser.id) {
          try {
            const anonDocRef = doc(db, 'user', anonUser.id);
            await updateDoc(anonDocRef, { request: [] });
          } catch (e) {}
          localStorage.setItem(
            'userMenu',
            JSON.stringify({ id: anonUser.id, name: 'anonimo' })
          );
        }
      } catch (e) {}
    }
  };

  const handleFinishEditFromList = async (orderItem) => {
    try {
      const orderRef = doc(db, 'requests', orderItem.id);
      const docSnap = await getDoc(orderRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const items = data.request || [];
        const nowStr = `${new Date().toLocaleDateString(
          'pt-BR'
        )} ${new Date().toLocaleTimeString('pt-BR')}`;
        const updatedItems = items.map((it, idx) => ({
          ...it,
          sentToKitchen: true,
          sentToKitchenTime: it.sentToKitchenTime || nowStr,
          parentRequestId: orderItem.id,
          indexInRequest:
            it.indexInRequest !== undefined ? it.indexInRequest : idx,
        }));

        await updateDoc(
          orderRef,
          cleanObject({
            request: updatedItems,
            done: true,
          })
        );

        if (data.idUser) {
          try {
            const userRef = doc(db, 'user', data.idUser);
            if (data.tableNumber || data.mesa) {
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                const uData = userSnap.data();
                const existingUserRequests = Array.isArray(uData.request) ? uData.request : [];
                const otherItems = existingUserRequests.filter(
                  (it) => it.parentRequestId && it.parentRequestId !== orderItem.id
                );
                await updateDoc(userRef, { request: [...otherItems, ...updatedItems] });
              } else {
                await updateDoc(userRef, { request: updatedItems });
              }
            } else {
              await updateDoc(userRef, { request: [] });
            }
          } catch (e) {
            console.warn(
              'Erro ao limpar carrinho do usuário ao finalizar edição pela lista:',
              e
            );
          }
        }
      }
    } catch (e) {
      console.error('Erro ao finalizar edição pela lista:', e);
    } finally {
      await handleReleaseEditFromList();
    }
  };

  const handleRequestItemCancellation = async (order, itemIndex) => {
    if (!canCancelOrders) {
      alert(
        'Você não tem permissão para cancelar itens do pedido. Consulte o gerente.'
      );
      return;
    }
    const dish = order.request[itemIndex];

    const isInKitchen = order.tableNumber || order.mesa || order.paymentDone;

    if (!isInKitchen) {
      const isConfirmed = window.confirm(
        `Deseja cancelar o item "${dish.name}"?`
      );
      if (!isConfirmed) return;

      try {
        const orderRef = doc(db, 'requests', order.id);
        const itemPrice = Number(dish.finalPrice) || 0;

        const updatedRequest = [...order.request];
        const arrayIndexToRemove = updatedRequest.findIndex(
          (i) => i.indexInRequest === itemIndex
        );
        if (arrayIndexToRemove !== -1) {
          updatedRequest.splice(arrayIndexToRemove, 1);
        }

        const newTotal = Math.max(
          0,
          (Number(order.finalPriceRequest) || 0) - itemPrice
        );

        await updateDoc(orderRef, {
          request: updatedRequest,
          finalPriceRequest: newTotal,
        });

        if (order.idUser) {
          const userRef = doc(db, 'user', order.idUser);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.request && Array.isArray(userData.request)) {
              const updatedUserRequest = userData.request.filter(
                (userItem) =>
                  !(
                    userItem.parentRequestId === order.id &&
                    userItem.indexInRequest === itemIndex
                  )
              );
              await updateDoc(userRef, { request: updatedUserRequest });
            }
          }
        }
        alert('Item cancelado com sucesso.');
      } catch (err) {
        console.error('Erro ao cancelar item:', err);
        alert('Erro ao cancelar item. Tente novamente.');
      }
    } else {
      const isConfirmed = window.confirm(
        `Deseja solicitar o cancelamento do item "${dish.name}" para a cozinha?`
      );
      if (!isConfirmed) return;

      try {
        const orderRef = doc(db, 'requests', order.id);
        const updatedRequest = [...order.request];
        updatedRequest[itemIndex] = { ...dish, cancelRequested: true };

        await updateDoc(orderRef, {
          request: updatedRequest,
        });
        alert('Solicitação de cancelamento enviada para a cozinha.');
      } catch (err) {
        console.error('Erro ao solicitar cancelamento:', err);
        alert('Erro ao solicitar cancelamento. Tente novamente.');
      }
    }
  };

  const handleDishRefusalSubmit = async ({
    dish,
    order,
    itemIndex,
    refusalType,
    refusalReason,
  }) => {
    try {
      const orderRef = doc(db, 'requests', order.id);
      const updatedRequest = [...order.request];

      const targetIndex = updatedRequest.findIndex(
        (r, idx) =>
          (r.indexInRequest !== undefined && r.indexInRequest === itemIndex) ||
          idx === itemIndex
      );

      if (targetIndex === -1) {
        alert('Prato não encontrado no pedido.');
        return;
      }

      const currentDish = updatedRequest[targetIndex];
      const statusLabel =
        refusalType === 'ajuste' ? 'Em ajuste' : 'Prato recusado';

      updatedRequest[targetIndex] = {
        ...currentDish,
        refusalRequested: true,
        refusalType,
        refusalReason,
        refusalTimestamp: Date.now(),
        status: statusLabel,
      };

      await updateDoc(orderRef, {
        request: updatedRequest,
      });

      if (order.idUser) {
        try {
          const userDocRef = doc(db, 'user', order.idUser);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            if (userData.request && Array.isArray(userData.request)) {
              const newUserDataRequests = userData.request.map((uReq) => {
                const isParentMatch =
                  !uReq.parentRequestId || uReq.parentRequestId === order.id;
                if (isParentMatch) {
                  const match =
                    (uReq.indexInRequest !== undefined &&
                      uReq.indexInRequest === itemIndex) ||
                    (uReq.name === currentDish.name && uReq.entregue);
                  if (match) {
                    return {
                      ...uReq,
                      refusalRequested: true,
                      refusalType,
                      refusalReason,
                      refusalTimestamp: Date.now(),
                      status: statusLabel,
                    };
                  }
                }
                return uReq;
              });
              await updateDoc(userDocRef, { request: newUserDataRequests });
            }
          }
        } catch (userErr) {
          console.warn('Erro ao sincronizar recusa no documento user:', userErr);
        }
      }

      setRefusalModalData(null);
      alert(
        `Solicitação de ${
          refusalType === 'ajuste' ? 'ajuste' : 'recusa'
        } enviada para a cozinha com sucesso!`
      );
    } catch (err) {
      console.error('Erro ao enviar recusa de prato:', err);
      alert('Erro ao registrar recusa de prato. Tente novamente.');
    }
  };

  return {
    refusalModalData,
    setRefusalModalData,
    handleEditOrder,
    handleReleaseEditFromList,
    handleFinishEditFromList,
    handleRequestItemCancellation,
    handleDishRefusalSubmit,
  };
};

